import type { ReactNode } from 'react';
import { Children, useEffect, useRef, useState } from 'react';
import {
  Platform,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { ScrollView, View, XStack, YStack } from 'tamagui';

import PSIcon from '@/components/ui/PSIcon';
import PSText from '@/components/ui/PSText';
import { space } from '@/constants/tokens';

// Web arrow button diameter.
const ARROW = 32;
// Reserved width for EACH number in the "current / total" counter — a
// `minWidth`, not a fixed `width`. Just reserving the whole string's width
// wasn't enough: "1 / 28" -> "12 / 28" kept the chevrons still (the outer
// box didn't grow) but the "/" itself visibly shifted, because the string
// was centered as one block and "12" is wider than "1". Splitting into
// three pieces — number, "/", number — with the LEFT number right-aligned
// and the RIGHT number left-aligned in their own reserved boxes fixes that:
// each grows away from the "/", not through it, so the "/" and the digit
// right next to it never move for the common (up to 2-digit, ~20-ish
// cards) case. `minWidth`, not `width`, so a 3rd digit or a larger system
// font size still grows past this floor rather than clipping — not an
// enforced cap, just a sizing estimate for the case that shouldn't shift.
const COUNTER_NUMBER_MIN_WIDTH = 20;
// Sub-pixel tolerance for "did the measured width/scroll position actually
// change" checks near a scroll boundary. Verified empirically (instrumented
// click-through logging): the browser's own layout reads wobble by a couple
// px across the final animation frames as a scroll settles, even though the
// true rest position never moves — too small a tolerance here re-derives
// `atEnd` (and the chevron/dot state built on it) several times on noise
// alone, which reads as the last chevron flickering in and out.
const EDGE_EPSILON = 4;

/**
 * Carousel — a horizontal, edge-to-edge scrolling card row (molecule).
 *
 * The home screen wraps its content in a `ScrollView` padded by the app gutter
 * (`px="$xl"`). A plain horizontal row inside that would start and end *at the
 * gutter*, so it couldn't scroll its cards flush to the screen edges — the row
 * would look inset. The design wants cards to bleed to both edges while the
 * *first* card still lines up with the page gutter.
 *
 * This is the "trick" we used to inline at every carousel and are now naming:
 *   1. **Bleed** — `mx={-space[gutter]}` cancels the parent's horizontal padding
 *      so the row spans the full screen width and can scroll edge to edge.
 *   2. **Re-inset** — the leading gutter comes back as real left padding, and the
 *      trailing gutter as right padding, so the first and last cards align to the
 *      app gutter again.
 *
 * Why real `pl` + `contentContainerStyle.paddingRight` and NOT
 * `contentContainerStyle.paddingHorizontal`: RN drops one side of a horizontal
 * `paddingHorizontal` once the content overflows, clipping the first card with no
 * leading gutter. Splitting it into an explicit left pad (on the scroll view) and
 * a right pad (on the content container) avoids that.
 *
 * This is a Tamagui `ScrollView`, so `$token` strings resolve on its props —
 * unlike RN's ScrollView, no `getTokenValue` bridge is needed here (see the token
 * trap in docs/building-components.md). The bleed margin is a raw `space[gutter]`
 * value because it's a *negative* number — there's no negative-token syntax, and
 * this mirrors what every call site already did (`mx={-space.xl}`).
 *
 * `gutter` is the parent screen's horizontal padding — the amount to cancel and
 * re-inset. Defaults to `$xl` (the app gutter every current caller uses); pass a
 * different space token if a screen pads by something else. Constrained to the
 * space scale so an off-scale value can't compile.
 *
 * Render-only and content-agnostic: the caller passes the cards as `children`; we
 * own the scroll + the gap+stretch row so a call site is just `<Carousel>…cards
 * </Carousel>`. `items="stretch"` makes every card match the tallest in the row.
 * `gap` isn't a prop — no call site needs a different one; add it the day one does
 * (no speculative variants).
 *
 * Desktop web has no swipe gesture, so on `Platform.OS === 'web'` this also
 * renders a control row centered beneath the carousel: {left chevron}
 * {"1 / 8" counter} {right chevron}. (Touch-capable mobile web still gets
 * this row too — but it's inert extra UI there, not a regression: swipe
 * still works, the row is just unnecessary.) Native is untouched: no row at
 * all, just the plain scroll view.
 *
 * One counted step per card: each child is wrapped to measure its own
 * x-offset in the row (`onLayout`, not an assumed fixed width — cards
 * aren't all the same width), so a chevron press scrolls to the exact next
 * card's left edge rather than an arbitrary distance. A chevron is hidden —
 * not just dimmed — at whichever end has nothing further that way, matching
 * District's reference; with only one card there's nothing to page through
 * at all, so the whole row is skipped.
 *
 * `isBackgroundDark` — same flag `PSHomeSectionError`/`PSHomeSectionEmpty`
 * already use for the dark access-pass bands — swaps the counter's color for
 * that surface (`$textDarkMuted` → `$textWhiteMuted`), matching how those
 * two components already branch for the same bands.
 */
type SpaceToken = Exclude<keyof typeof space, 'true'>;

export default function Carousel({
  children,
  gutter = 'lg',
  isBackgroundDark = false,
}: {
  children: ReactNode;
  gutter?: SpaceToken;
  isBackgroundDark?: boolean;
}) {
  const cards = Children.toArray(children);

  const scrollRef = useRef<ScrollView>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);
  // Tracks the scrollX value as of the last time currentIndex was committed
  // — see the effect below, near where `liveIndex` is computed, for why.
  const scrollXAtLastCommit = useRef(scrollX);
  // Sparse: index i is set once card i's wrapper has laid out. `forEach`
  // below skips unset holes, so a card that hasn't measured yet just isn't a
  // nearest-index candidate rather than producing NaN.
  const [offsets, setOffsets] = useState<number[]>([]);
  // The debounced "which card is active" — see the effect below, near where
  // `liveIndex` is computed, for why this is debounced rather than derived
  // directly on every render.
  const [currentIndex, setCurrentIndex] = useState(0);

  const maxScroll = Math.max(0, contentWidth - containerWidth);
  // Only render on web, and only once we know the row actually overflows
  // (guarding containerWidth > 0 avoids a premature true from a race where
  // contentWidth measures before containerWidth does). A single card has
  // nothing to page through, so it gets no row even if somehow wider than
  // the viewport.
  const showAffordances =
    Platform.OS === 'web' &&
    containerWidth > 0 &&
    maxScroll > 1 &&
    cards.length > 1;

  const onLayout = (e: LayoutChangeEvent) =>
    setContainerWidth(e.nativeEvent.layout.width);

  const onContentLayout = (e: LayoutChangeEvent) =>
    setContentWidth(e.nativeEvent.layout.width);

  const onCardLayout = (i: number, e: LayoutChangeEvent) => {
    const x = e.nativeEvent.layout.x;
    setOffsets((prev) => {
      if (prev[i] === x) return prev;
      const next = [...prev];
      next[i] = x;
      return next;
    });
  };

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    setScrollX(contentOffset.x);
    // The scroll event's own size fields are authoritative — they're what
    // the browser/native scroll system actually clamps against, including
    // the trailing padding our own onLayout-based estimate below doesn't
    // see. Overwriting with these once scrolling starts is what makes the
    // `atEnd` check below line up with where the browser truly stops.
    //
    // Guarded to only update on a change past EDGE_EPSILON: this fires on
    // every scroll tick (~every 16ms during an animated scrollTo), and
    // re-reading sub-pixel-noisy layout values that many times mid-animation
    // was flipping `atEnd` in and out right around the boundary — confirmed
    // by instrumented logging: scrollLeft settled cleanly at a single value,
    // but `layoutMeasurement`/`contentSize` (and so `maxScroll`) kept
    // wobbling by a couple px for several more frames, which flipped the
    // right chevron's visibility off/on/off before it actually settled.
    setContainerWidth((prev) =>
      Math.abs(prev - layoutMeasurement.width) < EDGE_EPSILON
        ? prev
        : layoutMeasurement.width
    );
    setContentWidth((prev) =>
      Math.abs(prev - contentSize.width) < EDGE_EPSILON
        ? prev
        : contentSize.width
    );
  };

  // The card whose measured offset is nearest the current scroll position —
  // except right at the true end, where nearest-offset matching can get
  // stuck one card short: if the last card (plus trailing padding) is
  // narrower than the viewport, the browser never lets scrollX reach that
  // card's own offset — it's already fully visible before that — so
  // `nearest` would keep resolving to the second-to-last card forever.
  // Forcing the last index once truly at the end sidesteps that.
  const atEnd = maxScroll > 0 && scrollX >= maxScroll - EDGE_EPSILON;
  let liveIndex = atEnd ? cards.length - 1 : 0;
  if (!atEnd) {
    let nearest = Infinity;
    offsets.forEach((x, i) => {
      const d = Math.abs(x - scrollX);
      if (d < nearest) {
        nearest = d;
        liveIndex = i;
      }
    });
  }

  // Distinguishes a real scroll (chevron press, swipe — scrollX itself
  // moves) from a post-settle relayout (a card further down an unscrolled
  // row can still be loading its image, and that can nudge the row's real
  // content width — and hence `maxScroll`/`atEnd` — a beat after the scroll
  // itself has already stopped moving, confirmed by instrumented logging:
  // scrollX sat frozen while `atEnd` still flipped). Only the latter needs
  // debouncing — that's what stops the last chevron flickering in and out,
  // by collapsing a burst of rapid changes into whichever value is still
  // standing 120ms later. Applying that same delay to every index change
  // was making the counter visibly lag ~120ms behind the card row actually
  // finishing its scroll, which read as a stutter — a real scroll updates
  // immediately, in sync with the row's own motion.
  useEffect(() => {
    const movedByScrolling = scrollXAtLastCommit.current !== scrollX;
    scrollXAtLastCommit.current = scrollX;

    if (movedByScrolling) {
      setCurrentIndex(liveIndex);
      return;
    }

    const id = setTimeout(() => setCurrentIndex(liveIndex), 120);
    return () => clearTimeout(id);
    // scrollX is read via the ref above on purpose, not as a dependency —
    // depending on it directly would rerun this effect on every ~16ms
    // scroll tick even when liveIndex itself hasn't changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveIndex]);

  const canScrollLeft = currentIndex > 0;
  const canScrollRight = currentIndex < cards.length - 1;
  const counterColor = isBackgroundDark ? '$textWhiteMuted' : '$textDarkMuted';

  const goToCard = (i: number) => {
    const clamped = Math.max(0, Math.min(cards.length - 1, i));
    const x = offsets[clamped] ?? 0;
    // Clamp to maxScroll: whenever the last card (plus trailing padding) is
    // narrower than the viewport, its own offset sits PAST the browser's
    // true scroll limit — you don't need to scroll that far to fully reveal
    // it. Requesting an out-of-range scrollTo doesn't error, but it visibly
    // rubber-bands/overscrolls-and-springs-back instead of settling cleanly.
    // This used to be unclamped because `maxScroll` itself was wrong (missed
    // the trailing padding, see the onScroll comment above) and clamping to
    // it made the last card unreachable; now that `maxScroll` is sourced
    // from the real `scrollWidth`/`offsetWidth`, clamping to it is correct
    // again and avoids the bounce.
    scrollRef.current?.scrollTo({
      x: Math.min(x, maxScroll),
      animated: true,
    });
  };

  return (
    <YStack gap="$md">
      <View mx={-space[gutter]}>
        <ScrollView
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          onLayout={onLayout}
          onScroll={onScroll}
          scrollEventThrottle={16}
          // Bleed past the parent gutter, then re-inset the leading gutter as
          // real left padding. The negative margin (on the wrapping `View`
          // above) is a raw value (no negative-token syntax); `pl` is a real
          // Tamagui prop on this Tamagui ScrollView, so the `$space.${gutter}`
          // token string resolves there — verified against the raw DOM:
          // the first card lands exactly `gutter` px in, at every scroll
          // position.
          pl={`$space.${gutter}`}
        >
          {/* The card row. Owned here so callers only pass cards. items="stretch"
              makes every card match the tallest in the row. `onLayout` here
              (not the ScrollView's `onContentSizeChange`) is what measures the
              true content width on web — react-native-web's ScrollView
              reports `onContentSizeChange` as the clipped viewport size, not
              the full scrollable width, regardless of how much content
              overflows it. Each card is wrapped in an `XStack` (not a plain
              `View`) whose own `onLayout` reports that card's x-offset in
              the row — see `onCardLayout` — so the control row can scroll to
              an exact card rather than a fixed distance. It has to be row-
              direction + stretch itself, matching the outer row: a plain
              `View` defaults to column direction, whose "stretch" cross-axis
              is WIDTH, not height — so a taller sibling stretched the
              wrapper's height (as this row's own alignItems="stretch"
              intends) without that height reaching the actual card inside
              it, leaving a shorter card's own panel visibly shorter than the
              row while the wrapper around it silently ate the extra space. */}
          <XStack alignItems="stretch" onLayout={onContentLayout}>
            <XStack gap="$lg" alignItems="stretch">
              {cards.map((card, i) => (
                <XStack
                  key={i}
                  alignItems="stretch"
                  onLayout={(e) => onCardLayout(i, e)}
                >
                  {card}
                </XStack>
              ))}
            </XStack>
            {/* The trailing gutter as a real element, not `paddingRight` on
                the content container — verified with a raw DOM dump: the
                browser's own `scrollWidth` silently drops one side of a
                container's horizontal padding once the content overflows
                (present in computed style, absent from the actual scrollable
                area), so no matter what prop or value carried it, the last
                card's trailing padding was invisible once actually scrolled
                to. A plain element's width always counts, so this sidesteps
                the bug entirely instead of chasing which prop drops it. */}
            <View width={space[gutter]} />
          </XStack>
        </ScrollView>
      </View>

      {/* Web-only control row, centered beneath the carousel: chevron,
          "current / total" counter, chevron. Never overlaid on the cards
          themselves — the cards' own viewport is already tight, so a
          floating arrow would cover real content (an image, a price)
          instead of sitting in dead space the way it can over
          ImageCarousel's full-bleed photo. Both chevron slots always mount
          — only `visible` toggles — so hiding one at an end doesn't shrink
          the row's total width and shift the counter sideways (mounting/
          unmounting one did exactly that). */}
      {showAffordances ? (
        <XStack items="center" justify="center" gap="$md">
          <Arrow
            side="left"
            visible={canScrollLeft}
            onPress={() => goToCard(currentIndex - 1)}
          />
          <XStack items="center" gap="$xs">
            <PSText.Tags
              minWidth={COUNTER_NUMBER_MIN_WIDTH}
              textAlign="right"
              color={counterColor}
            >
              {currentIndex + 1}
            </PSText.Tags>
            <PSText.Tags color={counterColor}>/</PSText.Tags>
            <PSText.Tags
              minWidth={COUNTER_NUMBER_MIN_WIDTH}
              textAlign="left"
              color={counterColor}
            >
              {cards.length}
            </PSText.Tags>
          </XStack>
          <Arrow
            side="right"
            visible={canScrollRight}
            onPress={() => goToCard(currentIndex + 1)}
          />
        </XStack>
      ) : null}
    </YStack>
  );
}

// Exported so other web-only swipe affordances (e.g. `PolaroidCardStack`)
// can reuse the same chevron look instead of duplicating it.
export function Arrow({
  side,
  visible,
  onPress,
}: {
  side: 'left' | 'right';
  visible: boolean;
  onPress: () => void;
}) {
  return (
    <View
      cursor={visible ? 'pointer' : undefined}
      onPress={visible ? onPress : undefined}
      opacity={visible ? 1 : 0}
      pointerEvents={visible ? 'auto' : 'none'}
      height={ARROW}
      width={ARROW}
      rounded="$full"
      items="center"
      justify="center"
      backgroundColor="$background"
      borderWidth="$borderWidth.hairline"
      borderColor="$borderColor"
    >
      <PSIcon
        name={side === 'left' ? 'chevron-back' : 'chevron-forward'}
        size={16}
      />
    </View>
  );
}
